import React from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import type { OnboardingCompany } from '../../../../../types/Onboarding'

/** Up to two initials for the company badge, e.g. "Open Provider" → "OP". */
export function companyInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? '').slice(0, 2)
  return letters.toUpperCase() || '?'
}

/**
 * Shown to someone whose email domain belongs to a company already on Sevv (SC-438). One domain
 * means one company, so the only way forward is to join it. A full company can't be joined, and
 * the person is told to ask their admin.
 */
export default function JoinCompanyStep({
  company,
  emailDomain,
  busy,
  onJoin,
}: {
  company: OnboardingCompany | null
  emailDomain: string | null
  busy: boolean
  onJoin: () => void
}) {
  const name = company?.name || 'Your company'
  const members = company?.memberCount ?? 0

  return (
    <Box data-testid="onboarding-join-step">
      <Typography variant="h5" component="h1" fontWeight={700}>
        Your team’s already here
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        {emailDomain ? (
          <>
            We matched your email domain <b>@{emailDomain}</b> to an office that’s already on Sevv.
          </>
        ) : (
          'We matched your email domain to an office that’s already on Sevv.'
        )}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          p: 2,
          borderRadius: 3,
          border: '2px solid',
          borderColor: company?.full ? 'divider' : 'primary.main',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 800,
            fontSize: 20,
          }}
        >
          {companyInitials(name)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography fontWeight={700} fontSize={17}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {members === 1 ? '1 teammate already inside' : `${members} teammates already inside`}
          </Typography>
        </Box>
        {!company?.full && (
          <Button variant="contained" size="large" disabled={busy} onClick={onJoin}>
            Join office
          </Button>
        )}
      </Box>

      {company?.full && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {name} has no free seats right now. Ask your admin to make room, then sign in again.
        </Alert>
      )}
    </Box>
  )
}
